import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';

const ALL_IDENTIFIERS = Object.freeze([constants.ALL_BOOKS, constants.ALL_COURSES]);

const hasFilterValue = (value) => value !== undefined && value !== null && value !== '' && value !== false;

/**
 * Maps audit search fields onto their OTEL columns so one search covers both:
 * requestId → traceId, identifierId/bookId (book) → primaryId.
 * @param {Object} payload
 * @returns {Object}
 */
export const toOtelSearchPayload = (payload = {}) => {
  const { requestId, bookId, identifierId } = payload;
  const bookOrIdentifier = bookId || identifierId;
  return {
    ...payload,
    ...(hasFilterValue(requestId) && { traceId: requestId }),
    ...(hasFilterValue(bookOrIdentifier) &&
      !ALL_IDENTIFIERS.includes(bookOrIdentifier) && { primaryId: bookOrIdentifier })
  };
};

/** Trends cluster rows carry the trace id as `requestId`; telemetry grid rows use `traceId`. */
export const getRowTraceId = (row) => row?.traceId || row?.requestId;

export const getTelemetryQueryParams = (row) => {
  const params = new URLSearchParams({ [constants.URL_PARAMS.TRACE_ID]: getRowTraceId(row) });
  const { isStackB, env } = utils.getStackBParams();
  if (isStackB) params.append('stackB', 'true');
  if (env) params.append('env', env);
  return params;
};

export const getTelemetryRowFromSearch = (search) => {
  const query = new URLSearchParams(search);
  return {
    // Observe unified detail uses requestid (optionally with sdk=true); dedicated telemetry page still uses traceid
    traceId:
      query.get(constants.URL_PARAMS.TRACE_ID) || query.get('traceId') || query.get(constants.URL_PARAMS.REQUEST_ID)
  };
};

/** Tenant log-source mode from AI_ADMIN_PORTAL_OTEL_TENANT, or null when the tenant is not configured there. */
export const getOtelMode = (tenant) =>
  Object.values(constants.OTEL_MODES).includes(tenant?.otel) ? tenant.otel : null;

/** OTEL-only (strict) tenants; drives the OTEL-specific screens (Telemetry, OTEL trends, clustering, counts). */
export const isOtelTenant = (tenant) => getOtelMode(tenant) === constants.OTEL_MODES.STRICT;

/** Tenants whose logs include OTEL traces (on or strict). */
export const hasOtelLogs = (tenant) =>
  [constants.OTEL_MODES.ON, constants.OTEL_MODES.STRICT].includes(getOtelMode(tenant));

/** Tenants configured in AI_ADMIN_PORTAL_OTEL_TENANT search Observe through the v3 API with cursor paging. */
export const usesObserveV3 = (tenant) => getOtelMode(tenant) !== null;

/** Drops grid columns whose `otelModes` config does not include the tenant's otel mode. */
export const filterColumnsByOtelMode = (columns = [], tenant) => {
  const mode = getOtelMode(tenant);
  return columns.filter((column) => !Array.isArray(column.otelModes) || column.otelModes.includes(mode));
};

export const isTelemetryEnabled = (tenant, selectedTab) => isOtelTenant(tenant) && selectedTab?.apiVersion === 'v3';

export const stripTelemetryDetailFields = (data) => {
  if (!data || typeof data !== 'object') return data;
  const { rawResponse, spanTree, langGraphSteps, ...rest } = data;
  return rest;
};

const tryParseJson = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[' && trimmed[0] !== '"')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const flattenSpanTree = (spans, acc = []) => {
  if (!Array.isArray(spans)) return acc;
  spans.forEach((span) => {
    if (span && typeof span === 'object') {
      acc.push(span);
      if (span.children?.length) flattenSpanTree(span.children, acc);
    }
  });
  return acc;
};

const collectToolSpans = (tracingData) => {
  const { spanTree, langGraphSteps } = tracingData || {};
  let spans = flattenSpanTree(spanTree);
  if (!spans.length && Array.isArray(langGraphSteps)) {
    langGraphSteps.forEach((step) => {
      if (step?.spans?.length) spans = spans.concat(flattenSpanTree(step.spans));
    });
  }
  return spans.filter((span) => (span?.observationType || '').toLowerCase() === 'tool');
};

/**
 * Prefer the tool message `content` when completion is a tool-result envelope.
 */
export const extractToolCallOutputValue = (completion) => {
  if (completion == null) return null;
  const parsed = tryParseJson(completion);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.content != null) {
    return tryParseJson(parsed.content);
  }
  return parsed;
};

/**
 * Maps API toolCallOutput (`args` / `id`) onto display fields and fills missing
 * output from matching tool spans in the trace tree.
 */
export const enrichToolCallOutput = (toolCallOutput, tracingData) => {
  if (!Array.isArray(toolCallOutput) || !toolCallOutput.length) return toolCallOutput || [];

  const spansByName = {};
  collectToolSpans(tracingData).forEach((span) => {
    const key = span.name || '';
    if (!spansByName[key]) spansByName[key] = [];
    spansByName[key].push(span);
  });

  return toolCallOutput.map((tool) => {
    if (!tool || typeof tool !== 'object') return tool;

    const queue = spansByName[tool.name] || [];
    const span = queue.length ? queue.shift() : null;

    let input = null;
    if (tool.input != null) input = tryParseJson(tool.input);
    else if (tool.args != null) input = tryParseJson(tool.args);
    else if (span?.prompt != null) input = tryParseJson(span.prompt);

    let output = null;
    if (tool.output != null) output = tryParseJson(tool.output);
    else if (tool.content != null) output = tryParseJson(tool.content);
    else output = extractToolCallOutputValue(span?.completion);

    return {
      ...tool,
      toolCallId: tool.toolCallId || tool.id || null,
      input: input ?? null,
      output: output ?? null,
      durationMs: tool.durationMs != null ? tool.durationMs : span?.durationMs ?? null
    };
  });
};

/**
 * Normalizes v3 detail API envelopes into display + tracing payloads.
 */
export const normalizeTelemetryDetailResponse = (apiBody) => {
  if (!apiBody || typeof apiBody !== 'object') {
    return { displayData: {}, tracingData: {} };
  }

  let trace = apiBody;
  if (apiBody.status === 'success' && apiBody.data && typeof apiBody.data === 'object') {
    trace = apiBody.data;
  } else if (apiBody.data?.traceId && !apiBody.traceId) {
    trace = apiBody.data;
  }

  if (!trace || typeof trace !== 'object') {
    return { displayData: {}, tracingData: {} };
  }

  const tracingData = {
    spanTree: trace.spanTree,
    langGraphSteps: trace.langGraphSteps,
    requestId: trace.metadata?.requestId,
    traceId: trace.traceId
  };

  const displayData = stripTelemetryDetailFields(trace) || {};
  if (Array.isArray(displayData.toolCallOutput)) {
    displayData.toolCallOutput = enrichToolCallOutput(displayData.toolCallOutput, tracingData);
  }

  return {
    displayData,
    tracingData
  };
};

export const mapTelemetryToResponseDataValue = (detail) => ({
  botResponseArr: detail?.botResponseArr,
  botResponse: detail?.aiResponse,
  type: detail?.name,
  displayType: detail?.name || detail?.serviceName
});

const decodePythonString = (value) => value.replace(/\\n/g, '\n').replace(/\\'/g, "'");

const tryExtractJsonInput = (text) => {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && parsed.input != null) {
      return String(parsed.input);
    }
  } catch {
    // Not JSON — keep extracted content as-is.
  }
  return text;
};

/**
 * Returns user-facing input text from telemetry detail question values.
 * Extracts HumanMessage content when present; otherwise returns the original string.
 */
export const extractTelemetryUserInputText = (question) => {
  if (question == null) return '';
  if (typeof question !== 'string') return String(question);

  const trimmed = question.trim();
  if (!trimmed) return '';

  const humanMatch = trimmed.match(/HumanMessage\(content=(['"])([\s\S]*?)\1,\s*additional_kwargs/);
  if (humanMatch) {
    return tryExtractJsonInput(decodePythonString(humanMatch[2]));
  }

  return trimmed;
};

export default {
  getTelemetryQueryParams,
  getTelemetryRowFromSearch,
  isTelemetryEnabled,
  stripTelemetryDetailFields,
  extractToolCallOutputValue,
  enrichToolCallOutput,
  normalizeTelemetryDetailResponse,
  mapTelemetryToResponseDataValue,
  extractTelemetryUserInputText
};
